import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
import {
  AgentSettingsChip,
  type AgentSettingsStatusMessage,
} from '@reflex/ui/components/agent/AgentSettingsChip';
import { useProviderSubscriptionAffordance } from '@/components/model-providers/useProviderSubscriptionAffordance';
import { toModelProvider } from '@/components/agent/to-model-provider';
import { useAgentTypeRegistry } from '@/lib/agent-type-registry';
import { useIsDesktop } from '@reflex/ui/hooks/useIsDesktop';
import { useAgentModelSupportHelpers } from '@/hooks/config/useAgentModelSupport';
import { useOrganization } from '@/hooks/auth/useOrganization';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import type { AgentModelEndpoint, ModelProviderSecretType } from '@reflex/shared';
import type { AgentType } from '@/lib/types';
import { useEffectiveModelEndpoint } from '@/components/agent/launch-agent-dialog/useEffectiveModelEndpoint';

export interface AgentSettingsChipContainerProps {
  agentType: AgentType;
  /**
   * `null` is the same as "no override" — the underlying matrix may resolve a
   * different effective provider based on the model. Pass the user's
   * deliberate selection here.
   */
  selectedProviderId: string | null;
  model: string;
  modelEndpointId: string | null;
  reasoningEffort: string | null;
  onAgentTypeChange: (next: AgentType) => void;
  onModelChange: (modelId: string) => void;
  onReasoningEffortChange: (effort: string) => void;
  onEndpointChange: (endpointId: string) => void;
  /** See {@link AgentConfigFormSection}'s prop of the same name. */
  launchableEndpointsOverride?: AgentModelEndpoint[];
  providerSecretId?: string | null;
  onProviderSecretChange?: (id: string | null) => void;
  providerKeyOwner?: {
    ownerId: string;
  };
  /**
   * When true and the provider requires a key but none is selected, the chip
   * surfaces a "Provider key required" inline error. Pre-submit callers leave
   * this false: the picker is highlighted instead of dumping a big inline
   * error into the form. Post-submit the dialog flips this on so the user
   * sees an explicit reason their launch was blocked.
   */
  showMissingKeyError?: boolean;
  /**
   * Status copy when {@link showMissingKeyError} is true. Defaults to the
   * launch wording; persona save passes its own.
   */
  missingKeyErrorText?: string;
  /**
   * When true, collapse the model segment to its provider icon on narrow
   * viewports (the provider-key segment already collapses this way). Set by the
   * composer surfaces that render the chip in the tight `ChatInput` toolbar so
   * it stays on one row; the inline config form leaves it false to keep the
   * readable model name.
   */
  compactOnMobile?: boolean;
  className?: string;
}

const DEFAULT_MISSING_KEY_ERROR = 'Choose a provider key to launch this agent.';

/**
 * Self-contained renderer for the agent settings chip (agent type / provider /
 * model / reasoning / endpoint + provider key). Performs all of the
 * matrix/registry resolution from the supplied `agentType`/`model` props and
 * renders the shared, popover-driven `AgentSettingsChip`.
 *
 * Extracted from `AgentConfigFormSection` so the same chip can be hosted in two
 * places: the Persona editor renders it inline (via `AgentConfigFormSection`),
 * and the launch composers render it in the `ChatInput` toolbar while telling
 * `AgentConfigFormSection` to omit its own copy.
 */
export function AgentSettingsChipContainer({
  agentType,
  selectedProviderId,
  model,
  modelEndpointId,
  reasoningEffort,
  onAgentTypeChange,
  onModelChange,
  onReasoningEffortChange,
  onEndpointChange,
  launchableEndpointsOverride,
  providerSecretId,
  onProviderSecretChange,
  providerKeyOwner,
  showMissingKeyError = false,
  missingKeyErrorText = DEFAULT_MISSING_KEY_ERROR,
  compactOnMobile = false,
  className,
}: AgentSettingsChipContainerProps) {
  const agentTypeRegistry = useAgentTypeRegistry();
  const modelSupport = useAgentModelSupportHelpers();
  const isDesktop = useIsDesktop();
  const { activeOrganizationId } = useOrganization();
  const routes = useOrgRoutes();

  const isMatrixDriven = modelSupport.isMatrixDriven(agentType);
  const supportEntry = modelSupport.getEntry(agentType);
  const unavailableMessage =
    supportEntry?.status === 'unavailable' ? (supportEntry.message ?? null) : null;

  const availableProviders = useMemo(
    () => modelSupport.getProvidersForAgent(agentType),
    [modelSupport, agentType],
  );

  const effectiveProviderId = useMemo(() => {
    if (!isMatrixDriven || availableProviders.length === 0) return null;
    if (selectedProviderId && availableProviders.some((p) => p.id === selectedProviderId)) {
      return selectedProviderId;
    }
    if (model) {
      const modelProviderId = modelSupport.getProviderForModel(agentType, model);
      if (availableProviders.some((p) => p.id === modelProviderId)) {
        return modelProviderId;
      }
    }
    const curatedDefault = modelSupport.getDefaultProvider(agentType);
    if (curatedDefault && availableProviders.some((p) => p.id === curatedDefault)) {
      return curatedDefault;
    }
    return availableProviders[0]?.id ?? null;
  }, [isMatrixDriven, availableProviders, selectedProviderId, model, modelSupport, agentType]);

  const modelOptions = useMemo(() => {
    if (isMatrixDriven) {
      return modelSupport.getDiscoveredModels(agentType).map((m) => ({
        id: m.id,
        name: m.displayName,
        providerId: m.providerId,
        recommended: m.recommended,
      }));
    }
    return agentTypeRegistry.get(agentType)?.models ?? [];
  }, [isMatrixDriven, modelSupport, agentType, agentTypeRegistry]);

  const providerId = useMemo(
    () => modelSupport.getProviderForModel(agentType, model),
    [modelSupport, agentType, model],
  );
  const providerEntry = useMemo(
    () => modelSupport.getProviderEntry(agentType, effectiveProviderId),
    [modelSupport, agentType, effectiveProviderId],
  );
  const keyTypes = useMemo<readonly ModelProviderSecretType[]>(
    () => providerEntry?.keyTypes ?? ['apiKey'],
    [providerEntry],
  );
  const requiresKey = keyTypes.length > 0;
  const disabledKeyTypes = useMemo<ModelProviderSecretType[]>(
    () => (['apiKey', 'subscription'] as const).filter((t) => !keyTypes.includes(t)),
    [keyTypes],
  );
  const keyPickerProvider = useMemo(
    () => toModelProvider(providerEntry?.modelProvider ?? effectiveProviderId ?? providerId, model),
    [providerEntry, effectiveProviderId, providerId, model],
  );
  const showProviderKeyPicker = onProviderSecretChange != null && requiresKey;
  const missingRequiredKey = showProviderKeyPicker && !providerSecretId;
  const allEndpoints = useMemo(
    () => (isMatrixDriven ? modelSupport.getEndpointsForProvider(agentType, providerId) : []),
    [isMatrixDriven, modelSupport, agentType, providerId],
  );
  const availableEndpoints = useMemo(
    () => allEndpoints.filter((e) => modelSupport.isEndpointAvailable(agentType, e.id)),
    [allEndpoints, modelSupport, agentType],
  );
  const showEndpointSelect = isMatrixDriven && allEndpoints.length > 1;

  const { effectiveModelEndpointRouteId, hasNoAvailableEndpoint } = useEffectiveModelEndpoint({
    isMatrixDriven,
    modelEndpointId,
    agentType,
    model,
    allEndpoints,
    availableEndpoints: launchableEndpointsOverride ?? availableEndpoints,
    modelSupport,
  });

  const reasoningOptions = useMemo(
    () => agentTypeRegistry.get(agentType)?.reasoningOptions ?? [],
    [agentTypeRegistry, agentType],
  );

  // Select whatever the user just registered from inside the picker. The
  // sole-candidate default only covers a first-ever key; with other keys
  // already present the new subscription would otherwise appear in the list
  // unselected, which reads as the registration not having taken. Only a
  // registration is a choice, though: updating an existing subscription from
  // its cog repairs that credential and says nothing about which key this
  // launch should use, so it leaves the selection alone.
  const {
    affordance: subscriptionAffordance,
    manage: subscriptionManage,
    dialog: subscriptionDialog,
  } = useProviderSubscriptionAffordance(keyPickerProvider, {
    onRegistered: (id, mode) => {
      if (id && mode === 'register') onProviderSecretChange?.(id);
    },
  });

  const agentTypeOptions = useMemo(
    () =>
      agentTypeRegistry
        .getAll()
        .filter(
          (info) =>
            info.type === agentType || modelSupport.isAgentTypeEnabled(info.type as AgentType),
        )
        .map((info) => ({ id: info.type, name: info.name })),
    [agentTypeRegistry, agentType, modelSupport],
  );
  const modelSelectOptions = useMemo(
    () =>
      modelOptions.map((m) => ({
        id: m.id,
        name: m.name,
        ...('providerId' in m && m.providerId ? { providerId: m.providerId } : {}),
        ...('recommended' in m && m.recommended ? { recommended: true as const } : {}),
      })),
    [modelOptions],
  );
  const endpointSelectOptions = useMemo(
    () =>
      showEndpointSelect
        ? allEndpoints.map((endpoint) => {
            const isAvailable = modelSupport.isEndpointAvailable(agentType, endpoint.id);
            const reason = modelSupport.getEndpointAvailabilityMessage(agentType, endpoint.id);
            return {
              id: endpoint.id,
              name: endpoint.displayName,
              disabled: !isAvailable,
              unavailableReason: !isAvailable ? (reason ?? undefined) : undefined,
            };
          })
        : undefined,
    [showEndpointSelect, allEndpoints, modelSupport, agentType],
  );
  const modelControlDisabled = modelOptions.length === 0;
  const statusMessages = useMemo(() => {
    const messages: AgentSettingsStatusMessage[] = [];
    if (unavailableMessage) {
      messages.push({ id: 'unavailable', tone: 'error', text: unavailableMessage });
    }
    if (isMatrixDriven && modelOptions.length === 0) {
      messages.push({
        id: 'no-models',
        tone: 'muted',
        text: 'No launchable models are available for this agent.',
      });
    }
    // When the provider needs a key and the user hasn't picked one, the
    // endpoint-route check fails for the same reason. Surfacing the verbose
    // "no compatible endpoint route" error in that case is misleading — the
    // highlighted picker (and, after a submit attempt, the explicit
    // missing-key message below) tell the real story.
    if (hasNoAvailableEndpoint && !missingRequiredKey) {
      messages.push({
        id: 'no-endpoint',
        tone: 'error',
        text: 'No compatible endpoint route is available for the selected model. Check endpoint credentials or admin policy.',
      });
    }
    if (missingRequiredKey && showMissingKeyError) {
      messages.push({
        id: 'missing-key',
        tone: 'error',
        text: missingKeyErrorText,
      });
    }
    return messages;
  }, [
    unavailableMessage,
    isMatrixDriven,
    modelOptions.length,
    hasNoAvailableEndpoint,
    missingRequiredKey,
    showMissingKeyError,
    missingKeyErrorText,
  ]);

  return (
    <AgentSettingsChip
      className={className}
      compact={compactOnMobile && !isDesktop}
      agentType={agentType}
      agentTypeOptions={agentTypeOptions}
      onAgentTypeChange={(next) => onAgentTypeChange(next as AgentType)}
      model={model}
      modelOptions={modelSelectOptions}
      onModelChange={onModelChange}
      modelDisabled={modelControlDisabled}
      modelFooter={
        <Link
          to={routes.agentModels}
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <Settings2 className="h-3 w-3" />
          Manage models
        </Link>
      }
      preferredProviderId={effectiveProviderId}
      providerOrder={availableProviders.map((p) => p.id)}
      reasoningOptions={reasoningOptions}
      reasoningEffort={reasoningEffort}
      onReasoningEffortChange={onReasoningEffortChange}
      endpointOptions={endpointSelectOptions}
      endpointValue={effectiveModelEndpointRouteId}
      onEndpointChange={onEndpointChange}
      statusMessages={statusMessages}
      providerKey={
        showProviderKeyPicker
          ? {
              value: providerSecretId ?? null,
              onChange: (v) => onProviderSecretChange?.(v),
              provider: keyPickerProvider,
              allowedTypes: ['apiKey', 'subscription'],
              disabledTypes: disabledKeyTypes,
              owner: providerKeyOwner,
              storageKey: `mps:lastPick:${activeOrganizationId ?? 'none'}:${keyPickerProvider}`,
              onEmptyAddHref: routes.modelProvidersPersonal,
              allowCreate: true,
              addSubscription: subscriptionAffordance,
              manageSubscription: subscriptionManage,
              dialog: subscriptionDialog,
              // On narrow viewports the full "Provider · name [Key]" string
              // starves the agent/model segments of width, so collapse the
              // key segment to its icon-only form (full details stay in the
              // picker popover).
              compact: !isDesktop,
              highlight: missingRequiredKey,
            }
          : undefined
      }
    />
  );
}
